// src/pages/Quote/Quote.jsx
// FRONT OFFICE - Quote page with Firebase integration

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts } from '../../contexts/ProductContext';
import { getProductById } from '../../services/ProductService';
import { collection, getDocs, query, where, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import './Quote.css';

const Quote = () => {
  // Sample pharmacy products database for quotes
  const pharmacyProducts = [
    { id: 'PH-1001', name: 'Ibuprofen 200mg', category: 'Pain Relief' },
    { id: 'PH-1002', name: 'Lisinopril 10mg', category: 'Blood Pressure' },
    { id: 'PH-1003', name: 'Metformin 500mg', category: 'Diabetes' },
    { id: 'PH-1004', name: 'Amoxicillin 250mg', category: 'Antibiotic' },
    { id: 'PH-1005', name: 'Atorvastatin 20mg', category: 'Cholesterol' },
  ];

  // Quotes that can include product references
  const productQuotes = [
    { text: "Remember to take your {product} as prescribed for optimal results.", author: "Pharmacy Tip" },
    { text: "{product} should be stored at room temperature away from moisture.", author: "Storage Advice" },
    { text: "Ask your pharmacist about potential side effects of {product}.", author: "Safety Reminder" },
    { text: "Don't forget to refill your {product} before you run out.", author: "Refill Reminder" },
    { text: "Take {product} with food if it causes stomach upset.", author: "Usage Tip" },
  ];

  const generalQuotes = [
    { text: "Your health is our top priority. We're here to help you feel your best.", author: "Pharmacy Team" },
    { text: "Always consult your pharmacist about potential drug interactions.", author: "Safety Reminder" },
    { text: "Proper medication storage ensures effectiveness and safety.", author: "Pharmacy Tip" },
  ];

  const [currentQuote, setCurrentQuote] = useState({ text: '', author: '' });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [bgColor, setBgColor] = useState('#f0f8ff');
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [requestedProduct, setRequestedProduct] = useState(null);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    productName: '',
    quantity: '',
    customMessage: '',
  });
  
  const { currentUser } = useAuth();
  const { products: contextProducts } = useProducts();
  const productParam = searchParams.get('product');

  // Load product data from Firebase
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // If we have products from context, use those
        if (contextProducts && contextProducts.length > 0) {
          setProducts(contextProducts);
        } else {
          // Otherwise, fetch from Firestore
          const productsQuery = query(collection(db, "products"));
          const querySnapshot = await getDocs(productsQuery);
          const productsList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setProducts(productsList);
        }
      } catch (error) {
        console.error("Error loading products:", error);
      }
    };

    loadProducts();
  }, [contextProducts]);

  // Check for product from URL params
  useEffect(() => {
    const fetchProductFromParam = async () => {
      if (productParam) {
        try {
          const productData = await getProductById(productParam);
          setRequestedProduct(productData);
          setQuoteFormData(prev => ({
            ...prev,
            productName: productData.name
          }));
        } catch (error) {
          console.error("Error fetching product:", error);
        }
      }
    };

    fetchProductFromParam();
  }, [productParam]);

  useEffect(() => {
    generateQuote();
  }, [selectedProduct]);

  const generateQuote = () => {
    setIsLoading(true);
    
    setTimeout(() => {
      let newQuote;
      const useProductQuote = selectedProduct && Math.random() > 0.3; // 70% chance to use product quote if product selected
      
      if (useProductQuote) {
        // Try to find product from our Firebase products first
        const product = products.find(p => p.id === selectedProduct) || 
                       pharmacyProducts.find(p => p.id === selectedProduct);
        
        if (product) {
          const randomQuote = productQuotes[Math.floor(Math.random() * productQuotes.length)];
          newQuote = {
            text: randomQuote.text.replace('{product}', product.name),
            author: randomQuote.author,
            productId: product.id
          };
        } else {
          // Fallback to general quotes if product not found
          newQuote = generalQuotes[Math.floor(Math.random() * generalQuotes.length)];
        }
      } else {
        newQuote = generalQuotes[Math.floor(Math.random() * generalQuotes.length)];
      }
      
      setCurrentQuote(newQuote);
      setIsLoading(false);
      
      // Change background color
      const colors = ['#f0f8ff', '#fff0f5', '#f0fff0', '#fff8dc', '#e6e6fa'];
      setBgColor(colors[Math.floor(Math.random() * colors.length)]);
    }, 500);
  };

  const shareQuote = () => {
    const shareText = `"${currentQuote.text}" - ${currentQuote.author}` + 
                     (currentQuote.productId ? ` (Product ID: ${currentQuote.productId})` : '');
    
    if (navigator.share) {
      navigator.share({
        title: 'Pharmacy Quote',
        text: shareText
      }).catch(err => {
        console.log('Error sharing:', err);
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      alert('Share this quote: ' + shareText);
    }
  };

  const handleProductChange = (e) => {
    setSelectedProduct(e.target.value);
  };

  const handleQuoteFormChange = (e) => {
    const { name, value } = e.target;
    setQuoteFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      // Create quote request in Firestore
      await addDoc(collection(db, "quoteRequests"), {
        productName: quoteFormData.productName,
        quantity: quoteFormData.quantity,
        customMessage: quoteFormData.customMessage,
        userId: currentUser?.uid || null,
        userEmail: currentUser?.email || null,
        productId: requestedProduct?.id || null,
        status: 'pending',
        createdAt: Timestamp.now()
      });
      
      setRequestSubmitted(true);
    } catch (error) {
      console.error("Error submitting quote request:", error);
      alert("Failed to submit quote request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="quote-page">
      <div className="container">
        <div className="quote-page-header">
          <h1>Request a Custom Quote</h1>
          <p>Get personalized pricing for bulk orders or special requests</p>
        </div>

        <div className="quote-page-content">
          {/* Quote Generator Section */}
          <div className="quote-generator" style={{ backgroundColor: bgColor }}>
            <div className="quote-container">
              <h3>Pharmacy Wisdom</h3>
              {isLoading ? (
                <div className="loader"></div>
              ) : (
                <div className="quote-content">
                  <p className="quote-text">{currentQuote.text}</p>
                  <p className="quote-author">— {currentQuote.author}</p>
                  {currentQuote.productId && (
                    <p className="product-id">Product ID: {currentQuote.productId}</p>
                  )}
                </div>
              )}
              
              <div className="product-selector">
                <select 
                  value={selectedProduct} 
                  onChange={handleProductChange}
                  disabled={isLoading}
                >
                  <option value="">Select a product (optional)</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="buttons">
                <button 
                  className="share-btn" 
                  onClick={shareQuote}
                  disabled={isLoading || !currentQuote.text}
                >
                  Share Quote
                </button>
                <button 
                  className="new-quote-btn" 
                  onClick={generateQuote}
                  disabled={isLoading}
                >
                  {selectedProduct ? 'New Product Tip' : 'New Quote'}
                </button>
              </div>
            </div>
          </div>

          {/* Quote Request Form */}
          <div className="quote-request-form">
            {requestSubmitted ? (
              <div className="quote-success">
                <div className="success-icon">✓</div>
                <h2>Quote Request Submitted!</h2>
                <p>Thank you for your quote request. Our team will review your request and get back to you shortly with pricing information.</p>
                <button className="btn btn-primary" onClick={() => setRequestSubmitted(false)}>Submit Another Request</button>
              </div>
            ) : (
              <form onSubmit={handleQuoteSubmit}>
                <h3>{requestedProduct ? `Request a Quote for ${requestedProduct.name}` : 'Request a Quote'}</h3>
                
                <div className="form-group">
                  <label htmlFor="productName">Product Name</label>
                  <input 
                    type="text"
                    id="productName"
                    name="productName"
                    value={quoteFormData.productName}
                    onChange={handleQuoteFormChange}
                    required
                    placeholder="Enter product name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="quantity">Quantity Needed</label>
                  <input 
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={quoteFormData.quantity}
                    onChange={handleQuoteFormChange}
                    required
                    placeholder="Enter quantity"
                    min="1"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="customMessage">Additional Information</label>
                  <textarea 
                    id="customMessage"
                    name="customMessage"
                    value={quoteFormData.customMessage}
                    onChange={handleQuoteFormChange}
                    rows="4"
                    placeholder="Include any specific requirements or questions"
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary quote-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Request Quote'}
                </button>
                
                <p className="quote-note">
                  * Our team will contact you with a custom price quote based on your request.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quote;












// // src/pages/Quote.jsx
// import React, { useState, useEffect } from 'react';

// const PharmacyQuoteInterface = () => {
//   // Sample pharmacy products database
//   const pharmacyProducts = [
//     { id: 'PH-1001', name: 'Ibuprofen 200mg', category: 'Pain Relief' },
//     { id: 'PH-1002', name: 'Lisinopril 10mg', category: 'Blood Pressure' },
//     { id: 'PH-1003', name: 'Metformin 500mg', category: 'Diabetes' },
//     { id: 'PH-1004', name: 'Amoxicillin 250mg', category: 'Antibiotic' },
//     { id: 'PH-1005', name: 'Atorvastatin 20mg', category: 'Cholesterol' },
//   ];

//   // Quotes that can include product references
//   const productQuotes = [
//     { text: "Remember to take your {product} as prescribed for optimal results.", author: "Pharmacy Tip" },
//     { text: "{product} should be stored at room temperature away from moisture.", author: "Storage Advice" },
//     { text: "Ask your pharmacist about potential side effects of {product}.", author: "Safety Reminder" },
//     { text: "Don't forget to refill your {product} before you run out.", author: "Refill Reminder" },
//     { text: "Take {product} with food if it causes stomach upset.", author: "Usage Tip" },
//   ];

//   const generalQuotes = [
//     { text: "Your health is our top priority. We're here to help you feel your best.", author: "Pharmacy Team" },
//     { text: "Always consult your pharmacist about potential drug interactions.", author: "Safety Reminder" },
//     { text: "Proper medication storage ensures effectiveness and safety.", author: "Pharmacy Tip" },
//   ];

//   const [currentQuote, setCurrentQuote] = useState({ text: '', author: '' });
//   const [selectedProduct, setSelectedProduct] = useState('');
//   const [isLoading, setIsLoading] = useState(false);
//   const [bgColor, setBgColor] = useState('#f0f8ff');

//   useEffect(() => {
//     generateQuote();
//   }, []);

//   const generateQuote = () => {
//     setIsLoading(true);
    
//     setTimeout(() => {
//       let newQuote;
//       const useProductQuote = selectedProduct && Math.random() > 0.3; // 70% chance to use product quote if product selected
      
//       if (useProductQuote) {
//         const product = pharmacyProducts.find(p => p.id === selectedProduct);
//         const randomQuote = productQuotes[Math.floor(Math.random() * productQuotes.length)];
//         newQuote = {
//           text: randomQuote.text.replace('{product}', product.name),
//           author: randomQuote.author,
//           productId: product.id
//         };
//       } else {
//         newQuote = generalQuotes[Math.floor(Math.random() * generalQuotes.length)];
//       }
      
//       setCurrentQuote(newQuote);
//       setIsLoading(false);
      
//       // Change background color
//       const colors = ['#f0f8ff', '#fff0f5', '#f0fff0', '#fff8dc', '#e6e6fa'];
//       setBgColor(colors[Math.floor(Math.random() * colors.length)]);
//     }, 500);
//   };

//   const shareQuote = () => {
//     const shareText = `"${currentQuote.text}" - ${currentQuote.author}` + 
//                      (currentQuote.productId ? ` (Product ID: ${currentQuote.productId})` : '');
    
//     if (navigator.share) {
//       navigator.share({
//         title: 'Pharmacy Quote',
//         text: shareText
//       }).catch(err => {
//         console.log('Error sharing:', err);
//       });
//     } else {
//       // Fallback for browsers that don't support Web Share API
//       alert('Share this quote: ' + shareText);
//     }
//   };

//   const handleProductChange = (e) => {
//     setSelectedProduct(e.target.value);
//   };

//   return (
//     <div className="pharmacy-quote-app" style={{ backgroundColor: bgColor }}>
//       <div className="quote-container">
//         {isLoading ? (
//           <div className="loader"></div>
//         ) : (
//           <div className="quote-content">
//             <p className="quote-text">{currentQuote.text}</p>
//             <p className="quote-author">— {currentQuote.author}</p>
//             {currentQuote.productId && (
//               <p className="product-id">Product ID: {currentQuote.productId}</p>
//             )}
//           </div>
//         )}
        
//         <div className="product-selector">
//           <select 
//             value={selectedProduct} 
//             onChange={handleProductChange}
//             disabled={isLoading}
//           >
//             <option value="">Select a product (optional)</option>
//             {pharmacyProducts.map(product => (
//               <option key={product.id} value={product.id}>
//                 {product.name} ({product.id})
//               </option>
//             ))}
//           </select>
//         </div>
        
//         <div className="buttons">
//           <button 
//             className="share-btn" 
//             onClick={shareQuote}
//             disabled={isLoading || !currentQuote.text}
//           >
//             Share Quote
//           </button>
//           <button 
//             className="new-quote-btn" 
//             onClick={generateQuote}
//             disabled={isLoading}
//           >
//             {selectedProduct ? 'New Product Tip' : 'New Quote'}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// // CSS
// const styles = `
//   .pharmacy-quote-app {
//     font-family: 'Arial', sans-serif;
//     display: flex;
//     justify-content: center;
//     align-items: center;
//     min-height: 100vh;
//     margin: 0;
//     transition: background-color 0.5s ease;
//     padding: 20px;
//     box-sizing: border-box;
//   }
  
//   .quote-container {
//     background-color: white;
//     border-radius: 10px;
//     box-shadow: 0 10px 20px rgba(0,0,0,0.1);
//     padding: 30px;
//     width: 90%;
//     max-width: 600px;
//     text-align: center;
//     transition: transform 0.3s ease;
//   }
  
//   .quote-container:hover {
//     transform: translateY(-5px);
//   }
  
//   .quote-text {
//     font-size: 1.3rem;
//     line-height: 1.6;
//     margin-bottom: 15px;
//     color: #2c3e50;
//     font-weight: 500;
//     min-height: 80px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//   }
  
//   .quote-author {
//     font-size: 1rem;
//     color: #7f8c8d;
//     font-style: italic;
//     margin-bottom: 10px;
//   }
  
//   .product-id {
//     font-size: 0.9rem;
//     color: #3498db;
//     background: #f8f9fa;
//     padding: 5px 10px;
//     border-radius: 4px;
//     display: inline-block;
//     margin-bottom: 20px;
//   }
  
//   .product-selector {
//     margin: 20px 0;
//   }
  
//   .product-selector select {
//     width: 100%;
//     padding: 10px;
//     border-radius: 5px;
//     border: 1px solid #ddd;
//     font-size: 1rem;
//   }
  
//   .buttons {
//     display: flex;
//     justify-content: space-between;
//     gap: 15px;
//   }
  
//   button {
//     border: none;
//     border-radius: 5px;
//     padding: 10px 20px;
//     font-size: 1rem;
//     cursor: pointer;
//     transition: all 0.3s ease;
//     flex: 1;
//   }
  
//   .new-quote-btn {
//     background-color: #3498db;
//     color: white;
//   }
  
//   .new-quote-btn:hover {
//     background-color: #2980b9;
//   }
  
//   .new-quote-btn:disabled {
//     background-color: #bdc3c7;
//     cursor: not-allowed;
//   }
  
//   .share-btn {
//     background-color: #2ecc71;
//     color: white;
//   }
  
//   .share-btn:hover {
//     background-color: #27ae60;
//   }
  
//   .share-btn:disabled {
//     background-color: #bdc3c7;
//     cursor: not-allowed;
//   }
  
//   .loader {
//     border: 5px solid #f3f3f3;
//     border-top: 5px solid #3498db;
//     border-radius: 50%;
//     width: 30px;
//     height: 30px;
//     animation: spin 1s linear infinite;
//     margin: 50px auto;
//   }
  
//   @keyframes spin {
//     0% { transform: rotate(0deg); }
//     100% { transform: rotate(360deg); }
//   }
// `;

// // Add styles to the head
// const styleSheet = document.createElement("style");
// styleSheet.type = "text/css";
// styleSheet.innerText = styles;
// document.head.appendChild(styleSheet);

// export default PharmacyQuoteInterface;